import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Plus, Check, Grid, List } from 'lucide-react';
import useSubmitAssessment from '../hooks/useSubmitAssessment';
import Header from '../components/Header';
import ReportGenerationModal from '../components/ReportGenerationModal';

// ProgressBar Component
const ProgressBar = ({ progress }) => (
  <div className="w-full">
    <div className="flex justify-between text-sm text-gray-500 mb-2">
      <span>Progress</span>
      <span>{Math.round(progress)}%</span>
    </div>
    <div className="w-full h-1.5 bg-gray-100 rounded overflow-hidden">
      <div 
        className="h-full bg-red-500 transition-all duration-300" 
        style={{ width: `${progress}%` }} 
      />
    </div>
  </div>
);

// Group Report Generation Modal Component
const GroupReportGenerationModal = ({ setIsVisible, packageId, packageEvalId, packageDetails }) => {
  const navigate = useNavigate();
  
  useEffect(() => {
    if (packageDetails) {
      const timer = setTimeout(() => {
        navigate('/group-report', {
          state: {
            packageDetails,
            assessments: packageDetails.assessments
          }
        });
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [navigate, packageDetails]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
      <div className="bg-white rounded-xl p-8 max-w-md w-full mx-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Processing Group Assessment
          </h3>
          <p className="text-gray-500">
            We're processing your group assessment results...
          </p>
        </div>
      </div>
    </div>
  );
};

// View preference hook
const useViewPreference = () => {
  const [isGridView, setIsGridView] = useState(() => {
    const saved = localStorage.getItem('viewPreference');
    return saved !== null ? JSON.parse(saved) : true;
  });

  useEffect(() => {
    localStorage.setItem('viewPreference', JSON.stringify(isGridView));
  }, [isGridView]);

  return [isGridView, setIsGridView];
};

const AssessmentQuiz = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { 
    assessment: singleAssessment, 
    packageData,
    packageEvalId: initialPackageEvalId,
    accessToken 
  } = location.state || {};

  const [isGridView, setIsGridView] = useViewPreference();
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedOptions, setSelectedOptions] = useState({});
  const [tools, setTools] = useState([]);
  const [currentTool, setCurrentTool] = useState('');
  const [showToolsPage, setShowToolsPage] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [packageEvalId, setPackageEvalId] = useState(initialPackageEvalId);
  const [packageDetails, setPackageDetails] = useState(null);
  const [assessmentId, setAssessmentId] = useState(null);
  
  const [currentAssessmentIndex, setCurrentAssessmentIndex] = useState(0);
  const [assessments, setAssessments] = useState([]);
  const [loadingAssessment, setLoadingAssessment] = useState(false);
  const [completedAssessments, setCompletedAssessments] = useState([]);

  const { submitAssessment, loading: submitLoading } = useSubmitAssessment();

  // Initialize assessments
  useEffect(() => {
    const initializeAssessments = async () => {
      if (singleAssessment) {
        setAssessments([singleAssessment]);
      } else if (packageData?.assessments?.length) {
        setLoadingAssessment(true);
        try {
          const promises = packageData.assessments.map(async (assessmentId) => {
            const response = await fetch(
              `https://app.spiralreports.com/api/assessments/${assessmentId}`,
              {
                headers: {
                  'Authorization': `Bearer ${accessToken}`,
                  'Content-Type': 'application/json',
                },
              }
            );
            if (!response.ok) throw new Error('Failed to fetch assessment');
            const { data } = await response.json();
            return data;
          });

          const fetchedAssessments = await Promise.all(promises);
          setAssessments(fetchedAssessments);
        } catch (error) {
          console.error('Error fetching assessments:', error);
          setErrorMessage('Failed to load assessments. Please try again.');
        } finally {
          setLoadingAssessment(false);
        }
      }
    };

    initializeAssessments();
  }, [singleAssessment, packageData, accessToken]);

  const submitPackageEvaluation = async () => {
    try {
      // Use the existing package eval ID if available
      if (packageEvalId) {
        const detailsResponse = await fetch(`https://app.spiralreports.com/api/package-eval/${packageEvalId}`, {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
        });

        if (!detailsResponse.ok) {
          throw new Error('Failed to fetch package evaluation details');
        }

        const packageDetails = await detailsResponse.json();
        
        return {
          evalId: packageEvalId,
          packageDetails: packageDetails.data[0]
        };
      }

      // If no package eval ID exists, create a new one
      const response = await fetch('https://app.spiralreports.com/api/package-eval', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: packageData?.name,
          description: packageData?.description,
          package_id: packageData?._id,
          assessments: packageData?.assessments || []
        })
      });

      if (!response.ok) {
        throw new Error('Failed to submit package evaluation');
      }

      const firstResponse = await response.json();
      const newPackageEvalId = firstResponse.data._id;
      setPackageEvalId(newPackageEvalId);

      const detailsResponse = await fetch(`https://app.spiralreports.com/api/package-eval/${newPackageEvalId}`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!detailsResponse.ok) {
        throw new Error('Failed to fetch package evaluation details');
      }

      const packageDetails = await detailsResponse.json();
      
      return {
        evalId: newPackageEvalId,
        packageDetails: packageDetails.data[0]
      };
    } catch (error) {
      console.error('Error in package evaluation process:', error);
      throw error;
    }
  };

  const isGroupAssessment = Boolean(packageData);
  const currentAssessment = assessments[currentAssessmentIndex];
  const questionSet = currentAssessment?.questionSet || [];
  const currentQuestion = questionSet[currentQuestionIndex];
  const totalAssessments = assessments.length;
  const totalSteps = questionSet.length + (showToolsPage ? 0 : 1);
  const progress = showToolsPage ? 100 : ((currentQuestionIndex + 1) / totalSteps) * 100;

  const handleSubmit = async () => {
    const purpose = 'SELF';
    const assessmentData = {
      assessmentId: currentAssessment?.id,
      receipient: purpose,
      name: currentAssessment?.title,
      compliance: isGroupAssessment 
        ? packageData?.compliance 
        : currentAssessment?.compliance?.[0],
      package_eval_id: packageEvalId,
      questions: questionSet.map((question, questionIndex) => ({
        question: question.question,
        considerScore: question.considerScore,
        options: (selectedOptions[questionIndex] || []).map(optionText => 
          question.options.find(option => option.text === optionText)
        ),
      })),
    };
  
    try {
      const result = await submitAssessment(
        assessmentData, 
        tools,
        packageData?._id
      );
      
      if (isGroupAssessment) {
        setCompletedAssessments(prev => [...prev, result.data.id]);

        if (currentAssessmentIndex < totalAssessments - 1) {
          setCurrentAssessmentIndex(prev => prev + 1);
          setCurrentQuestionIndex(0);
          setSelectedOptions({});
          setShowToolsPage(false);
        } else {
          try {
            const response = await submitPackageEvaluation();
            setPackageDetails(response.packageDetails);
            setIsModalVisible(true);
          } catch (error) {
            setErrorMessage('Failed to complete package evaluation. Please try again.');
          }
        }
      } else {
        setAssessmentId(result.data.id);
        setIsModalVisible(true);
      }
    } catch (error) {
      console.error('Error submitting assessment:', error);
      setErrorMessage('Failed to submit assessment. Please try again.');
    }
  };

  const handleOptionToggle = (optionText) => {
    setSelectedOptions(prev => {
      const currentQuestionSelections = prev[currentQuestionIndex] || [];
      if (currentQuestionSelections.includes(optionText)) {
        return {
          ...prev,
          [currentQuestionIndex]: currentQuestionSelections.filter(text => text !== optionText)
        };
      } else {
        return {
          ...prev,
          [currentQuestionIndex]: [...currentQuestionSelections, optionText]
        };
      }
    });
  };

  const isOptionSelected = (optionText) => {
    return (selectedOptions[currentQuestionIndex] || []).includes(optionText);
  };

  const handleNextQuestion = () => {
    if ((selectedOptions[currentQuestionIndex] || []).length === 0) {
      setErrorMessage('Please select at least one option to proceed.');
      return;
    }

    setErrorMessage('');
    if (currentQuestionIndex === questionSet.length - 1) {
      setShowToolsPage(true);
    } else {
      setCurrentQuestionIndex(prev => prev + 1);
    }
  };

  const handlePreviousQuestion = () => {
    if (showToolsPage) {
      setShowToolsPage(false);
    } else if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
    }
  };

  const handleToolSubmit = (e) => {
    e.preventDefault();
    if (currentTool.trim() === '') {
      setErrorMessage('Tool name cannot be empty.');
      return;
    }
    if (tools.includes(currentTool.trim())) {
      setErrorMessage('Tool already added.');
      return;
    }
    setTools([...tools, currentTool.trim()]);
    setCurrentTool('');
    setErrorMessage('');
  };

  const handleToolDelete = (toolToDelete) => {
    setTools(tools.filter(tool => tool !== toolToDelete));
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleToolSubmit(e);
    }
  };

  if (loadingAssessment) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-500" />
      </div>
    );
  }

  return (
    <div>
      <Header />
      <div className="max-w-4xl mx-auto px-4 py-10 min-h-screen flex flex-col">
        {isGroupAssessment && (
          <div className="mb-6">
            <div className="text-sm text-gray-500 mb-2">
              Assessment {currentAssessmentIndex + 1} of {totalAssessments}
            </div>
            <div className="w-full h-2 bg-gray-100 rounded-full">
              <div
                className="h-full bg-red-500 rounded-full transition-all duration-300"
                style={{ width: `${(currentAssessmentIndex / totalAssessments) * 100}%` }}
              />
            </div>
          </div>
        )}

        <div className="bg-white rounded-xl shadow-md p-2 mb-16 flex-grow">
          <div className="sticky top-0 bg-white z-10 px-4 pt-20">
            <div className="mb-8">
              <ProgressBar progress={progress} />

              {!showToolsPage && currentQuestion && (
                <>
                  <div className="text-xs font-semibold text-red-500 uppercase tracking-wider mb-2">
                    Question {currentQuestionIndex + 1} of {questionSet.length}
                  </div>
                  <div className="text-lg font-semibold text-gray-900 mb-6 leading-relaxed">
                    {currentQuestion?.question}
                  </div>

                  <div className="flex justify-between items-center mb-6">
                    <div className="flex items-center">
                      <p className="text-sm text-gray-500 mr-2">View answers as:</p>
                      <button
                        onClick={() => setIsGridView(true)}
                        className={`p-2 rounded-lg flex items-center gap-2 ${
                          isGridView ? 'bg-red-100 text-red-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        <Grid size={18} />
                        <span className="text-sm font-medium">Grid</span>
                      </button>
                      <button
                        onClick={() => setIsGridView(false)}
                        className={`p-2 rounded-lg flex items-center gap-2 ml-2 ${
                          !isGridView ? 'bg-red-100 text-red-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        <List size={18} />
                        <span className="text-sm font-medium">List</span>
                      </button>
                    </div>
                    <div className="text-sm text-gray-500">
                      {(selectedOptions[currentQuestionIndex] || []).length} selected
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="mb-20">
            {!showToolsPage ? (
              <div className={`${
                isGridView 
                  ? 'grid grid-cols-1 md:grid-cols-2 gap-3' 
                  : 'flex flex-col gap-3'
              }`}>
                {currentQuestion?.options.map((option, index) => (
                  <div
                    key={index}
                    onClick={() => handleOptionToggle(option.text)}
                    className={`border rounded-lg cursor-pointer transition-all duration-200 overflow-hidden            
                    ${isOptionSelected(option.text) ? 'bg-red-50 border-red-200' : 'bg-white border-gray-200'}`}
                  >
                    <div className="flex items-start p-4 gap-3">
                      <div className={`w-5 h-5 rounded border-2 flex-shrink-0 mt-0.5 relative transition-all duration-200
                        ${isOptionSelected(option.text) ? 'bg-red-500 border-red-500' : 'border-gray-300'}`}>
                        {isOptionSelected(option.text) && 
                          <Check size={16} className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-white" />
                        }
                      </div>
                      <span className="text-gray-700 text-base leading-relaxed">{option.text}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div>
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">Tools Assessment</h2>
                <p className="text-base text-gray-500 mb-8 leading-relaxed">
                  Please list all the tools that are currently being used in your organization for asset management and security.
                </p>

                <div className="relative w-full mb-6">
                  <input
                    type="text"
                    value={currentTool}
                    onChange={(e) => setCurrentTool(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Type a tool name"
                    className="w-full p-3 rounded-lg border border-gray-300 text-base transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  />
                  {currentTool && (
                    <button
                      onClick={handleToolSubmit}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-xl text-gray-500 hover:text-gray-700 transition-all duration-200"
                      title="Add tool"
                    >
                      <Plus />
                    </button>
                  )}
                </div>

                <div className="flex flex-wrap gap-4 min-h-8">
                  {tools.map((tool, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center bg-red-100 px-6 py-3 rounded-lg text-base text-red-900 font-medium shadow-md mb-2"
                    >
                      {tool}
                      <button
                        onClick={() => handleToolDelete(tool)}
                        className="ml-3 text-red-900 hover:text-red-800 text-xl p-1"
                        title="Remove tool"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            )}

            {errorMessage && (
              <div className="text-red-500 text-sm mb-4">{errorMessage}</div>
            )}
          </div>

          {/* Navigation Buttons */}
          <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4">
            <div className="max-w-4xl mx-auto flex justify-between gap-4">
              <button
                onClick={handlePreviousQuestion}
                disabled={currentQuestionIndex === 0 && !showToolsPage}
                className={`px-6 py-3 rounded-md text-sm font-medium border border-gray-300 transition-all duration-200
                  ${currentQuestionIndex === 0 && !showToolsPage ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50'}`}
              >
                Previous
              </button>

              {!showToolsPage ? (
                <button
                  onClick={handleNextQuestion}
                  disabled={(selectedOptions[currentQuestionIndex] || []).length === 0}
                  className={`px-6 py-3 rounded-md text-sm font-medium bg-red-500 text-white hover:bg-red-600 transition-all duration-200
                    ${(selectedOptions[currentQuestionIndex] || []).length === 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  Next
                </button>
              ) : (
                <button
                  onClick={handleSubmit}
                  disabled={submitLoading}
                  className="px-6 py-3 rounded-md text-sm font-medium bg-red-500 text-white hover:bg-red-600 transition-all duration-200 disabled:opacity-50"
                >
                  {submitLoading 
                    ? 'Submitting...' 
                    : isGroupAssessment 
                      ? `Submit Assessment ${currentAssessmentIndex + 1}/${totalAssessments}`
                      : 'Submit Assessment'
                  }
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Modal */}
        {isModalVisible && (
          isGroupAssessment ? (
            <GroupReportGenerationModal 
              setIsVisible={setIsModalVisible} 
              packageId={packageData?._id}
              packageEvalId={packageEvalId}
              packageDetails={packageDetails}
            />
          ) : (
            <ReportGenerationModal 
              setIsVisible={setIsModalVisible}
              assessmentId={assessmentId}
            />
          )
        )}
      </div>
    </div>
  );
};

export default AssessmentQuiz;